import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { useFetch } from 'hooks';
import { AppLayout } from 'layouts';
import { Button, Date, Link, PageTitle, Tooltip } from 'components/atoms';
import { Pagination, Table } from 'components/molecules';
import { headings as configHeadings } from './config';
import { statusMapper } from 'services/status-mapper';
import { GatewayPermissions } from 'auth/constants/permissions';
import NextReviewDateModal from 'components/organisms/next-review-date-modal';
import { NoWrapText } from './TaskList.styles';
import { ButtonToggle } from 'components/atoms/button/Button.styles';
import { Redirect } from 'react-router-dom';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'services/applicationInsightsService';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import useDeepState from 'hooks/useDeepState';
import FilterContext from 'context/third-party-filter';
import { RegisteredClientUsers } from 'pages/third-party/types';

function TaskList() {
  const [sortingIndex, setSortingIndex] = useState(-1);
  const [isDescending, setIsDescending] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const { tokenInfo } = useAuth();
  const { hasPermission } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const { vantageWebApi } = useConfig();
  const isAdmin: boolean = hasPermission(GatewayPermissions.Admin);
  const [showAll, setShowAll] = useState<boolean>(isAdmin);
  const [filters, setFilters] = useDeepState({
    pageNumber: 1,
  });

  const selectedFiltersCount = useMemo(() => {
    const filtersCounter: Record<string, number> = {};
    for (const [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        filtersCounter[key] = value.length;
      }
    }
    return filtersCounter;
  }, [filters]);

  const hasViewTaskList: boolean = hasPermission(GatewayPermissions.ViewTaskList);

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useFetch(`${vantageWebApi}/account/team`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
  });

  const headings = useMemo(() => {
    configHeadings[1].name = terminologyConfig?.internalOwnerSingularNaming;
    configHeadings[2].name = terminologyConfig?.thirdPartySingularNaming;

    const owners = (Array.isArray(usersData) ? usersData : []) as RegisteredClientUsers[];

    const possibleOwnersOptions = owners.map((user) => ({
      name: `${user.firstName} ${user.lastName}`,
      value: user.id.toString(),
    }));

    if (Array.isArray(usersData)) {
      // enable filter when userData is available
      configHeadings[1].name = terminologyConfig?.internalOwnerPluralNaming;
      configHeadings[1].filter = {
        title: 'Filter by internal owners',
        field: 'internalOwnerIds',
        options: [
          { name: 'None', value: '00000000-0000-0000-0000-000000000000' },
          ...possibleOwnersOptions,
        ],
        height: '280px',
        width: '300px',
      };
      return [...configHeadings];
    }
  }, [usersData, terminologyConfig]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedThirdPartyid, setSelectedThirdPartyId] = useState(null);
  const {
    data,
    loading,
    error,
    trigger: taskSearchFetchLazy,
  } = useFetch(`${vantageWebApi}/task/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
    lazy: true,
    body: JSON.stringify({
      page: pageNumber,
      pageSize: 15,
      sort:
        sortingIndex >= 0 && headings?.[sortingIndex]?.field
          ? {
              sortType: headings?.[sortingIndex]?.field,
              isDescending,
            }
          : null,
      showAll,
    }),
  });

  useEffect(() => {
    if (!usersData) {
      return;
    }

    const controller = new AbortController();

    taskSearchFetchLazy({
      body: JSON.stringify({
        page: filters.pageNumber,
        pageSize: 15,
        filter: filters,
        sort:
          sortingIndex >= 0 && headings?.[sortingIndex]?.field
            ? {
                sortType: headings?.[sortingIndex]?.field,
                isDescending,
              }
            : null,
        showAll: showAll,
      }),
      signal: controller.signal,
    });

    sessionStorage.setItem('taskFilters', JSON.stringify(filters));
    sessionStorage.setItem(
      'taskSort',
      JSON.stringify({
        sortingIndex,
        isDescending,
      })
    );

    return () => controller.abort();
    // ignoring taskSearchFetchLazy, adding it will trigger the request twice
    // eslint-disable-next-line
  }, [vantageWebApi, showAll, sortingIndex, headings, isDescending, usersData, filters]);

  useEffect(() => {
    const storedFilters = sessionStorage.getItem('taskFilters');
    if (storedFilters) {
      setFilters(JSON.parse(storedFilters));
    }
    const storedSort = sessionStorage.getItem('taskSort');
    if (storedSort) {
      const { sortingIndex: storedSortingIndex, isDescending: storedIsDescending } =
        JSON.parse(storedSort);
      setSortingIndex(storedSortingIndex);
      setIsDescending(storedIsDescending);
    }
    // eslint-disable-next-line
  }, []);

  const shouldShowReportLink = (status) => {
    return status !== 'Next Review Date';
  };

  const hasReportAvailable = (status) => {
    return status.toLowerCase().includes('report ready for review');
  };

  const setTableOrder = ({ index }: { index: number }) => {
    if (sortingIndex === index) {
      if (isDescending === false) {
        setIsDescending(true);
      } else {
        setIsDescending(false);
        setSortingIndex(-1);
      }
      return;
    }
    setIsDescending(false);
    setSortingIndex(index);
  };

  const handleNextReviewModalOpen = (thirdPartyId) => {
    setSelectedThirdPartyId(thirdPartyId);
    setModalOpen(true);
  };

  const handleNextReviewModalClose = () => {
    setModalOpen(false);
  };

  const handleNextReviewModalProceed = () => {
    handleNextReviewModalClose();
  };

  const isLoading = loading || usersLoading;

  const hasError = error || usersError;

  return (
    <>
      {hasViewTaskList ? (
        <AppLayout bgColor="whiteSolid">
          <FilterContext.Provider value={[filters, setFilters]}>
            <PageTitle title="Task List">
              <ButtonToggle>
                {isAdmin && (
                  <Button
                    style={{ borderRadius: '0.3rem 0rem 0rem 0.3rem' }}
                    disabled={!showAll}
                    onClick={() => {
                      setShowAll(false);
                      setFilters({ ...filters, pageNumber: 1 });
                      setPageNumber(1);
                    }}
                  >
                    My tasks
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    style={{ borderRadius: '0rem 0.3rem 0.3rem  0rem' }}
                    disabled={showAll}
                    onClick={() => {
                      setShowAll(true);
                      setFilters({ ...filters, pageNumber: 1 });
                      setPageNumber(1);
                    }}
                  >
                    All tasks
                  </Button>
                )}
              </ButtonToggle>
              {data && data?.pageCount > 1 && (
                <Pagination
                  setCurrentPage={setPageNumber}
                  currentPage={pageNumber}
                  totalPages={data?.pageCount || 1}
                  onPageChange={({ currentPage }) => {
                    setFilters({ ...filters, pageNumber: currentPage });
                  }}
                />
              )}
            </PageTitle>
            <Table
              headings={headings}
              showPlaceholder={isLoading || hasError}
              onHeadingClick={setTableOrder}
              sortedColumn={sortingIndex}
              noResultsMessage="No matching Tasks"
              selectedFiltersCount={selectedFiltersCount}
            >
              {data?.results.map(({ createdDate, id, screeningRequest, thirdParty, status }) => (
                <tr key={id}>
                  <td>{createdDate && <Date dateTime={createdDate} format="MMM D, YYYY" />}</td>
                  <td>
                    {thirdParty?.owners?.length > 0 &&
                      thirdParty?.owners.map((x) => {
                        if (x.length > 15)
                          return (
                            <div key={x}>
                              <Tooltip tooltipText={x} placement="right" trigger="hover">
                                {x.length > 15 ? `${x.substring(0, 13)}..` : x}
                              </Tooltip>
                            </div>
                          );

                        return <NoWrapText key={x}>{x}</NoWrapText>;
                      })}
                  </td>
                  <td>
                    {thirdParty && (
                      <RouterLink to={`/third-party/profile/${thirdParty?.id}`}>
                        {thirdParty?.name || -thirdParty?.alternativeName || -thirdParty?.id}
                      </RouterLink>
                    )}
                  </td>
                  <td>{screeningRequest?.dueDiligenceType}</td>
                  <td>{screeningRequest?.dueDiligenceLevel}</td>
                  <td>
                    {shouldShowReportLink(status) ? (
                      <Link to={`/third-party/screening/${screeningRequest?.screeningId}`}>
                        {hasReportAvailable(status)
                          ? status
                          : statusMapper(
                              screeningRequest?.screeningReportStatusId,
                              screeningRequest?.currentScreeningStateId,
                              screeningRequest?.dueDiligenceLevel,
                              screeningRequest?.reportStatus
                            )}
                      </Link>
                    ) : (
                      <Button
                        text={true}
                        style={{ display: 'block' }}
                        onClick={() => handleNextReviewModalOpen(thirdParty?.id)}
                      >
                        <Link>{'Next Review Date Due'}</Link>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            {modalOpen && (
              <NextReviewDateModal
                isOpen={modalOpen}
                title={`Initiate ${terminologyConfig?.thirdPartySingularNaming} review now?`}
                onClose={handleNextReviewModalClose}
                loading={false}
                onProceed={handleNextReviewModalProceed}
                thirdPartyId={selectedThirdPartyid}
              />
            )}
          </FilterContext.Provider>
        </AppLayout>
      ) : (
        <Redirect to="/not-found" />
      )}
    </>
  );
}

export default withAITracking(reactPlugin, TaskList);
